import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BACKEND_ERROR_CODE_TRANSLATIONS } from '@/modules/common/api/errors/backend-error-codes';

export interface InviteFailure {
  name: string;
  email: string | null;
  errorCode?: number;
}

interface InviteFailuresDialogProps {
  failures: InviteFailure[];
  onClose: () => void;
}

export function InviteFailuresDialog({ failures, onClose }: InviteFailuresDialogProps) {
  const { t } = useTranslation('authenticated.settings');
  const { t: tTeam } = useTranslation('authenticated.team');
  const { t: tCommon } = useTranslation('common');

  return (
    <Dialog open={failures.length > 0} onOpenChange={open => !open && onClose()}>
      <DialogContent className="w-[90vw] max-w-2xl gap-0 p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{t('pages.members.inviteFailuresDialog.title')}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t('pages.members.inviteFailuresDialog.subtitle', { count: failures.length })}
          </p>
        </DialogHeader>

        <div className="max-h-[220px] overflow-y-auto">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[32%]" />
              <col className="w-[40%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                  {tTeam('name')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                  {tTeam('email')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                  {t('pages.members.inviteFailuresDialog.columnReason')}
                </th>
              </tr>
            </thead>
            <tbody>
              {failures.map((failure, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className="break-words px-4 py-3 font-medium">{failure.name}</td>
                  <td className="break-all px-4 py-3 text-muted-foreground">
                    {failure.email ?? tCommon('noValue')}
                  </td>
                  <td className="break-words px-4 py-3 text-destructive">
                    {failure.errorCode && BACKEND_ERROR_CODE_TRANSLATIONS[failure.errorCode]
                      ? t(BACKEND_ERROR_CODE_TRANSLATIONS[failure.errorCode])
                      : t('pages.members.inviteFailuresDialog.unknownError')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            {t('pages.members.inviteFailuresDialog.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
